import MarketingLayout, { MarketingProse } from "../components/MarketingLayout";

const SECTIONS = [
  {
    h: "1. Who we are",
    body: (
      <p>
        ezItemizer ("we", "us") is a parts inventory management service provided by
        ezItemizer Inc. Our website is{" "}
        <a href="https://ezitemizer.com" className="text-[#FF9800] underline">
          https://ezitemizer.com
        </a>
        . If you have questions about this policy, email{" "}
        <a href="mailto:info@ezitemizer.com" className="text-[#FF9800] underline">
          info@ezitemizer.com
        </a>
        .
      </p>
    ),
  },
  {
    h: "2. Information we collect",
    body: (
      <>
        <p>We collect three kinds of information:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li><strong>Account information</strong> — name, email, organization name, role, hashed password.</li>
          <li><strong>Inventory data</strong> — the parts, categories, stock counts, transactions, suppliers and locations you create in your account.</li>
          <li><strong>Usage data</strong> — IP address, browser type, device info, pages visited, and timestamps, collected via standard server logs.</li>
        </ul>
      </>
    ),
  },
  {
    h: "3. How we use it",
    body: (
      <ul className="list-disc pl-6 space-y-1.5">
        <li>To provide, maintain and improve the ezItemizer service.</li>
        <li>To authenticate you and keep your data scoped to your organization.</li>
        <li>To send transactional emails (welcome, password reset, low-stock alerts, end-of-day digests, billing receipts).</li>
        <li>To respond to support and feedback requests.</li>
        <li>To detect, prevent and respond to security incidents.</li>
      </ul>
    ),
  },
  {
    h: "4. Third-party processors",
    body: (
      <>
        <p>We use a small number of trusted vendors to operate ezItemizer:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li><strong>Stripe</strong> — billing, subscriptions and the customer portal. Stripe receives your name, email and payment details. We never see your full card number.</li>
          <li><strong>SendGrid (Twilio)</strong> — outbound transactional email. SendGrid sees the email address we send to plus the message body.</li>
          <li><strong>MongoDB Atlas</strong> — encrypted data storage.</li>
          <li><strong>Emergent</strong> — application hosting and infrastructure.</li>
        </ul>
        <p>We do not sell your data, ever.</p>
      </>
    ),
  },
  {
    h: "5. Cookies",
    body: (
      <p>
        We use first-party cookies only for authentication (a short-lived JWT access token and an httpOnly refresh-token cookie). We do not use third-party advertising or tracking cookies on the application. Stripe may set its own cookies on its hosted checkout and customer-portal pages — those are governed by{" "}
        <a href="https://stripe.com/privacy" className="text-[#FF9800] underline">Stripe's privacy policy</a>.
      </p>
    ),
  },
  {
    h: "6. Data retention",
    body: (
      <p>
        Your inventory data is retained for as long as your subscription is active. If you cancel, we keep your data for 30 days in case you reactivate, then permanently delete it. You can request immediate deletion of your account by emailing{" "}
        <a href="mailto:info@ezitemizer.com" className="text-[#FF9800] underline">info@ezitemizer.com</a>.
      </p>
    ),
  },
  {
    h: "7. Security",
    body: (
      <p>
        Passwords are stored using bcrypt with a per-password salt. All traffic to ezItemizer is served over HTTPS. We follow industry-standard practices to protect against unauthorized access, but no service can guarantee absolute security — we recommend you use a strong, unique password.
      </p>
    ),
  },
  {
    h: "8. Your rights",
    body: (
      <p>
        You can export your inventory data from the Reports page (CSV/PDF) at any time. You can request to access, correct or delete personal information about you by emailing{" "}
        <a href="mailto:info@ezitemizer.com" className="text-[#FF9800] underline">info@ezitemizer.com</a>. We will respond within 30 days.
      </p>
    ),
  },
  {
    h: "9. Children",
    body: (
      <p>
        ezItemizer is a business tool not directed at children under 16. We do not knowingly collect personal information from anyone under 16.
      </p>
    ),
  },
  {
    h: "10. Changes to this policy",
    body: (
      <p>
        If we make material changes, we will email account admins at the address on file at least 14 days before the change takes effect. The "Last updated" date above always reflects the current version.
      </p>
    ),
  },
];

export default function Privacy() {
  return (
    <MarketingLayout dataTestId="privacy-page">
      <MarketingProse
        kicker="Legal"
        title="Privacy Policy"
        lastUpdated="February 2026"
        dataTestId="privacy-prose"
      >
        <p className="text-lg">
          We take your privacy seriously. This page explains exactly what we collect, why, who we share it with, and what control you have over your data.
        </p>
        {SECTIONS.map((s) => (
          <section key={s.h}>
            <h2 className="font-display font-bold text-xl text-[#0F172A] mt-8 mb-3">{s.h}</h2>
            <div className="space-y-3">{s.body}</div>
          </section>
        ))}
      </MarketingProse>
    </MarketingLayout>
  );
}
